import { useState, useEffect } from 'react';
import AdminLayout from '../AdminLayout';
import { motion } from 'framer-motion';
import apiManager from '../../../../lib/api';
import { FunnelAnalytics } from '../../../../lib/api/services/analytics';

const FunnelAnalyticsPage = () => {
  const [funnelData, setFunnelData] = useState<FunnelAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<'week' | 'month' | 'quarter' | 'year'>('month');
  
  useEffect(() => {
    const fetchFunnelData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Obtener datos reales del backend
        const response = await apiManager.analytics.getFunnelAnalytics({
          period: dateRange
        });
        
        if (response.success && response.data) {
          setFunnelData(response.data);
        } else {
          setError(response.error?.message || 'Error al cargar los datos del embudo');
        }
      } catch (err) {
        console.error('Error al cargar datos del embudo:', err);
        setError('Error al cargar los datos. Inténtalo de nuevo más tarde.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchFunnelData();
  }, [dateRange]);
  
  return (
    <AdminLayout title="Análisis del Embudo de Conversión">
      {/* Selector de rango de fechas */}
      <div className="mb-6 flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-800">Embudo de Ventas</h2>
        <div className="flex bg-white rounded-md shadow-sm">
          <button
            onClick={() => setDateRange('week')}
            className={`px-4 py-2 text-sm rounded-l-md ${
              dateRange === 'week' 
                ? 'bg-indigo-600 text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Semana
          </button>
          <button
            onClick={() => setDateRange('month')}
            className={`px-4 py-2 text-sm ${
              dateRange === 'month' 
                ? 'bg-indigo-600 text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Mes
          </button>
          <button
            onClick={() => setDateRange('quarter')}
            className={`px-4 py-2 text-sm ${
              dateRange === 'quarter' 
                ? 'bg-indigo-600 text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Trimestre
          </button>
          <button
            onClick={() => setDateRange('year')}
            className={`px-4 py-2 text-sm rounded-r-md ${
              dateRange === 'year' 
                ? 'bg-indigo-600 text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Año
          </button>
        </div>
      </div>
      
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
        </div>
      ) : error ? (
        <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-600">
          <p>{error}</p>
        </div>
      ) : funnelData ? (
        <div className="space-y-6">
          {/* Resumen de conversión */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <motion.div 
              className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <div className="flex flex-col">
                <span className="text-sm font-medium text-gray-500">Visitantes Totales</span>
                <span className="text-3xl font-bold text-gray-900">{funnelData.totalVisitors.toLocaleString()}</span>
                <span className="text-xs text-gray-500 mt-2">
                  Periodo: {dateRange === 'week' ? 'última semana' : 
                            dateRange === 'month' ? 'último mes' : 
                            dateRange === 'quarter' ? 'último trimestre' : 'último año'}
                </span>
              </div>
            </motion.div>
            
            <motion.div 
              className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              <div className="flex flex-col">
                <span className="text-sm font-medium text-gray-500">Conversiones</span>
                <span className="text-3xl font-bold text-gray-900">{funnelData.totalConversions.toLocaleString()}</span>
                <span className="text-xs text-gray-500 mt-2">
                  Pedidos completados
                </span>
              </div>
            </motion.div>
            
            <motion.div 
              className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              <div className="flex flex-col">
                <span className="text-sm font-medium text-gray-500">Tasa de Conversión</span>
                <div className="flex items-center">
                  <span className="text-3xl font-bold text-gray-900">{funnelData.overallConversionRate.toFixed(1)}%</span>
                  <div className={`ml-3 ${
                    funnelData.overallConversionRate > funnelData.comparisonPeriodRate 
                      ? 'text-green-600' 
                      : 'text-red-600'
                  }`}>
                    <span className="text-sm font-medium">
                      {funnelData.overallConversionRate > funnelData.comparisonPeriodRate 
                        ? '+' 
                        : ''}
                      {(funnelData.overallConversionRate - funnelData.comparisonPeriodRate).toFixed(1)}%
                    </span>
                    <span className="text-xs text-gray-500 block">vs. periodo anterior</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
          
          {/* Visualización del embudo */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-6">Visualización del Embudo</h3>
            <div className="space-y-4">
              {funnelData.steps.map((step, index) => (
                <motion.div 
                  key={step.name}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className="relative"
                >
                  <div className="flex justify-between mb-1">
                    <div>
                      <span className="font-medium text-gray-800">{step.name}</span>
                      <span className="ml-2 text-sm text-gray-500">{step.count.toLocaleString()} visitantes</span>
                    </div>
                    <div className="flex items-center">
                      <span className="font-semibold text-gray-800">{step.rate.toFixed(1)}%</span>
                      {index > 0 && (
                        <span className={`ml-2 text-xs ${
                          step.rate > step.previousRate ? 'text-green-600' : 
                          step.rate < step.previousRate ? 'text-red-600' : 'text-gray-500'
                        }`}>
                          {step.rate > step.previousRate ? '+' : ''}
                          {(step.rate - step.previousRate).toFixed(1)}%
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="h-10 bg-gray-100 rounded-md overflow-hidden">
                    <div 
                      className={`h-full ${index === 0 ? 'bg-indigo-600' : 'bg-indigo-500'}`}
                      style={{ width: `${step.rate}%` }}
                    ></div>
                  </div>
                  {index < funnelData.steps.length - 1 && (
                    <div className="w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent border-t-gray-100 mx-auto my-1"></div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
          
          {/* Tasa de abandono por página */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Tasa de Abandono por Página</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Página
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Visitantes
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tasa de Abandono
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {funnelData.abandonmentByPage.map((page, index) => (
                    <motion.tr 
                      key={page.page}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {page.page}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {page.visitors.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className={`px-2 py-1 text-xs leading-5 font-semibold rounded-full ${
                            page.abandonmentRate > 50 ? 'bg-red-100 text-red-800' : 
                            page.abandonmentRate > 30 ? 'bg-yellow-100 text-yellow-800' : 
                            'bg-green-100 text-green-800'
                          }`}>
                            {page.abandonmentRate}%
                          </span>
                          <div className="ml-4 w-24 bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${
                                page.abandonmentRate > 50 ? 'bg-red-500' : 
                                page.abandonmentRate > 30 ? 'bg-yellow-500' : 
                                'bg-green-500'
                              }`}
                              style={{ width: `${page.abandonmentRate}%` }}
                            ></div>
                          </div>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Rendimiento por dispositivo */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Rendimiento por Dispositivo</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {funnelData.deviceBreakdown.map((device, index) => (
                <motion.div 
                  key={device.device}
                  className="bg-gray-50 p-4 rounded-lg"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-gray-800">{device.device}</h4>
                    <span className="bg-indigo-100 text-indigo-800 text-xs px-2 py-1 rounded-full font-semibold">
                      {device.rate.toFixed(1)}% conv.
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500 text-sm">Visitantes</span>
                      <span className="font-semibold">{device.visitors.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500 text-sm">Conversiones</span>
                      <span className="font-semibold">{device.conversions.toLocaleString()}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                      <div 
                        className="bg-indigo-600 h-1.5 rounded-full" 
                        style={{ width: `${(device.conversions / device.visitors) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <p className="text-gray-500">No hay datos disponibles</p>
        </div>
      )}
    </AdminLayout>
  );
};

export default FunnelAnalyticsPage; 